import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface AccessibilitySettings {
  textSize: number; // Base font size multiplier (1 = 100%, 1.2 = 120%, etc.)
  grayscale: boolean;
  highContrast: boolean;
  negativeContrast: boolean;
  lightBackground: boolean;
  linksUnderline: boolean;
  readableFont: boolean;
}

const DEFAULT_SETTINGS: AccessibilitySettings = {
  textSize: 1,
  grayscale: false,
  highContrast: false,
  negativeContrast: false,
  lightBackground: false,
  linksUnderline: false,
  readableFont: false,
};

@Injectable({
  providedIn: 'root',
})
export class AccessibilityService {
  private readonly STORAGE_KEY = 'accessibility-settings';
  /**
   * Text size is limited to two levels in both directions:
   * 1.0 (default), 1.1, 1.2 and 0.9, 0.8
   */
  private readonly TEXT_SIZE_STEP = 0.1;
  private readonly TEXT_SIZE_MIN = 0.8;
  private readonly TEXT_SIZE_MAX = 1.2;
  private settingsSubject = new BehaviorSubject<AccessibilitySettings>(
    this.loadSettings()
  );

  public settings$ = this.settingsSubject.asObservable();

  constructor() {
    // Apply settings on initialization
    this.applySettings(this.settingsSubject.value);
  }

  private loadSettings(): AccessibilitySettings {
    const saved = localStorage.getItem(this.STORAGE_KEY);
    return saved ? JSON.parse(saved) : DEFAULT_SETTINGS;
  }

  private saveSettings(settings: AccessibilitySettings): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(settings));
    this.settingsSubject.next(settings);
  }

  public updateSettings(partial: Partial<AccessibilitySettings>): void {
    const current = this.settingsSubject.value;
    const updated = { ...current, ...partial };
    this.saveSettings(updated);
    this.applySettings(updated);
  }

  public increaseText(): void {
    const current = this.settingsSubject.value.textSize;
    const next = Math.round((current + this.TEXT_SIZE_STEP) * 10) / 10;
    if (next <= this.TEXT_SIZE_MAX) {
      this.updateSettings({ textSize: next });
    }
  }

  public decreaseText(): void {
    const current = this.settingsSubject.value.textSize;
    const next = Math.round((current - this.TEXT_SIZE_STEP) * 10) / 10;
    if (next >= this.TEXT_SIZE_MIN) {
      this.updateSettings({ textSize: next });
    }
  }

  public toggleGrayscale(): void {
    const current = this.settingsSubject.value;
    this.updateSettings({ grayscale: !current.grayscale });
  }

  public toggleHighContrast(): void {
    const current = this.settingsSubject.value;
    this.updateSettings({ highContrast: !current.highContrast });
  }

  public toggleNegativeContrast(): void {
    const current = this.settingsSubject.value;
    this.updateSettings({ negativeContrast: !current.negativeContrast });
  }

  public toggleLightBackground(): void {
    const current = this.settingsSubject.value;
    this.updateSettings({ lightBackground: !current.lightBackground });
  }

  public toggleLinksUnderline(): void {
    const current = this.settingsSubject.value;
    this.updateSettings({ linksUnderline: !current.linksUnderline });
  }

  public toggleReadableFont(): void {
    const current = this.settingsSubject.value;
    this.updateSettings({ readableFont: !current.readableFont });
  }

  public reset(): void {
    this.saveSettings(DEFAULT_SETTINGS);
    this.applySettings(DEFAULT_SETTINGS);
  }

  private applySettings(settings: AccessibilitySettings): void {
    const root = document.documentElement;

    // Text Size
    root.style.fontSize = `${settings.textSize * 16}px`;

    // Grayscale
    if (settings.grayscale) {
      root.classList.add('accessibility-grayscale');
    } else {
      root.classList.remove('accessibility-grayscale');
    }

    // High Contrast
    if (settings.highContrast) {
      root.classList.add('accessibility-high-contrast');
    } else {
      root.classList.remove('accessibility-high-contrast');
    }

    // Negative Contrast
    if (settings.negativeContrast) {
      root.classList.add('accessibility-negative-contrast');
    } else {
      root.classList.remove('accessibility-negative-contrast');
    }

    // Light Background
    if (settings.lightBackground) {
      root.classList.add('accessibility-light-bg');
    } else {
      root.classList.remove('accessibility-light-bg');
    }

    // Links Underline
    if (settings.linksUnderline) {
      root.classList.add('accessibility-links-underline');
    } else {
      root.classList.remove('accessibility-links-underline');
    }

    // Readable Font
    if (settings.readableFont) {
      root.classList.add('accessibility-readable-font');
    } else {
      root.classList.remove('accessibility-readable-font');
    }
  }
}





